import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CheckCircle, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { ApplicationAccountStatus } from '@/common/models/applicationAccount'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { ApplicationAccountForm } from '../schemas/ApplicationAccountSchema'

export type PostNewApplicationAccountProps = {
  accountId: string
  applicationId: string
  tag: string[]
  status: ApplicationAccountStatus
  accountFields: Record<string, string> | null
  isMainAccount: boolean
}

async function postNewApplicationAccount(data: PostNewApplicationAccountProps) {
  return api.backend.post('/application-account', data)
}

export function useNewApplicationAccount() {
  const { t } = useTranslation('sso')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PostNewApplicationAccountProps
  >({
    mutationKey: ['/application-account'],
    mutationFn: postNewApplicationAccount,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: CheckCircle,
          title: t('accounts.use_new_application_account.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/account/paginate'] })
      queryClient.invalidateQueries({ queryKey: ['/application/findAll'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('accounts.use_new_application_account.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('accounts.use_new_application_account.toast_error.description'),
        },
      })
    },
  })

  const handleCreateApplicationAccount = async (
    accountId: string,
    data: ApplicationAccountForm,
  ) => {
    return mutateAsync({
      accountId,
      applicationId: data.applicationId,
      tag: data.tag.map((t) => t.value),
      accountFields: data.accountFields ? JSON.parse(data.accountFields) : null,
      status: ApplicationAccountStatus.PENDING,
      isMainAccount: data.isMainAccount,
    })
  }

  return {
    handleCreateApplicationAccount,
    isCreatingApplicationAccount: isPending,
  }
}
